import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

const ContactSchema = yup.object({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
})

const LineItemSchema = yup.object({
  itemNumber: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export enum LittlBeastLineItemKeys {
  itemNumber = 'itemNumber',
  itemName = 'itemName',
  description = 'description',
  itemQty = 'itemQty',
  unitPrice = 'unitPrice',
  lineTotal = 'lineTotal',
}

export const littleBeastPurchaseOrderFileDataSchema = yup.object({
  issuer: ContactSchema.required(),
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  poType: yup.string().nullable().optional(),
  deliveryLocation1: yup.object().shape({
    locationName: yup.string().nullable().optional(),
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    locationTotal: yup.string().nullable().optional(),
  }),
  deliveryLocation1LineItems: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.string().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  deliveryLocation2: yup.object().shape({
    locationName: yup.string().nullable().optional(),
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    locationTotal: yup.string().nullable().optional(),
  }),
  deliveryLocation2LineItems: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.string().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  deliveryLocation3: yup.object().shape({
    locationName: yup.string().nullable().optional(),
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    locationTotal: yup.string().nullable().optional(),
  }),
  deliveryLocation3LineItems: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.string().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  vendor: ContactSchema.required(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
})

export type LineItem = yup.InferType<typeof LineItemSchema>
export type LittlBeastPurchaseOrderFileData = yup.InferType<
  typeof littleBeastPurchaseOrderFileDataSchema
>
export type RequiredFileData = MakeRequiredAndNullable<LittlBeastPurchaseOrderFileData>

export const littlBeastFileMappings = {
  issuer: {
    attn: '9be00afa-2a89-4079-bfa2-a0dd21debbfb',
    companyName: '666d61fc-038a-49df-88d5-6355dc7ed2b6',
    addressLine1: '8a7eae49-76a6-417d-bf53-4dd43ed283f4',
    addressLine2: '531b29b1-d9d0-434d-99a3-5f6023dedb16',
    city: '97ec5b1a-34a8-4ae5-8e3a-0a3c8512d1df',
    state: '40670a84-89d5-4bf7-962e-a322e299ba0d',
    zipCode: '535c2ea1-2557-4a84-b038-c079cdb41a62',
    country: 'fd245317-f864-43c7-8d94-396799c9ca09',
    phoneNumber: 'ef221e1b-c499-49db-b1fc-39cfab8fbe4d',
    emailAddress: 'f3b66e5e-c635-40eb-a2a2-a5578b301db7',
  },
  orderNumber: '648a2045-dd84-4147-9c34-fe416fa3306c',
  paymentTerms: 'b4fd7443-f043-406a-86e2-0c7b1402e441',
  freightTerms: 'a0bcf893-1389-462f-95e1-2e7dc7480cbb',
  currency: '60e25ba6-6337-4b3e-9810-d1041cd86f22',
  poType: 'ddd7f35a-2df9-4ea3-807f-7bf5b83b3a47',
  dateIssued: 'aa1598d7-16e9-45bc-9ee5-81d17027827f',
  dateReady: '8db74c13-96e1-4d0c-a09e-99a620556bc0',
  vendor: {
    attn: 'b3e75fa5-9791-4cf5-8eed-f2f4446c1772',
    companyName: 'df791dd6-0c5d-4ce6-a50b-600898503fa1',
    addressLine1: '0d70f267-0849-44d9-9a1b-65875c168ddd',
    addressLine2: '2fc088b2-6ce2-42b4-921f-f7defe6085a6',
    city: 'ecb05618-c0d6-42c8-ae0e-3aabdb269ccf',
    state: '931a8315-77d9-46f1-b5b6-12e29e32cd4e',
    zipCode: '43ff1f4b-0091-46af-b5f9-747675116e96',
    country: '93f9abbc-82b0-4a60-a4c8-dca355993857',
    phoneNumber: '1b060341-5610-484e-94ff-b9dddfd9b255',
    emailAddress: '7b0c7e9a-ac1d-4f51-ae04-f5355523f7a4',
  },

  deliveryLocation1: {
    locationName: 'f575994b-2b83-4e2e-a216-749f47e3a600',
    attn: '59a27c4e-d599-48dc-a2c1-c0f81be2dc1a',
    companyName: '7d293b75-c8a7-4cef-b741-2828e6e6b913',
    addressLine1: '5df5a69d-c497-40b6-bcd5-0ea772f64807',
    addressLine2: '53b34b2f-1861-4ad5-bca9-e2d1e36fadeb',
    city: '28feaec6-c212-4234-b6bf-b96dbea6414a',
    state: '189d5da5-b785-40f9-b5d1-0bf40dca42ab',
    zipCode: '2f868ba5-e67e-4c5d-9c68-081331aadeca',
    country: 'fc3eb6fb-6395-4b86-87b4-2c90045c0592',
    phoneNumber: '0837e11f-8e27-4935-8592-4d5ebd95fb54',
    emailAddress: '8d5a2c6f-4e31-4425-93e4-7a8a68dc02df',
    locationTotal: '887731cf-5d0e-4b99-b13b-0a2b311c49b3',
  },
  deliveryLocation1LineItems: [
    {
      itemNumber: '08e27a9f-1e59-4c59-a909-a1cd3fe5b722',
      itemName: '45544e5a-4c9d-4b83-bbd1-425dd982165a',
      description: '5e60b8f7-71d3-4ee7-a478-a529d5e99dbc',
      itemQty: 'b5c1d012-f487-4d46-b6e1-73b249e583ff',
      unitPrice: 'd0b66861-bb85-471a-a349-f65dc7066b96',
      lineTotal: null,
    },
  ],
  deliveryLocation2: {
    locationName: 'f7b6a2e5-41e9-4d80-9a4f-f1698438b491',
    attn: '96ed4c6b-f0c2-4088-a86d-6aa826496cd5',
    companyName: '18c35085-4051-429d-9086-948a2f759fa3',
    addressLine1: 'de44e50d-52a4-4e1f-bac7-6788b0a08cf4',
    addressLine2: 'f9c8e0a9-4f2e-46b3-bb7f-12fd15a47b17',
    city: 'b69ac3a1-94a3-4c77-a9d3-79be2eab75b3',
    state: '88c321b0-f7e6-4df2-918b-b0f56a510310',
    zipCode: '3f420f1f-232e-4230-ae39-376218c1d0f1',
    country: '3762d110-f3a6-42a7-850f-dfd4fbeefea7',
    phoneNumber: '9d0d345f-531e-47d6-a71f-d17a6e1356ae',
    emailAddress: 'b94d2a4f-5cc4-412c-a91d-72f2aa8a0621',
    locationTotal: '8b2f6c39-cdc2-4b62-a1bc-b77d7056e9b0',
  },
  deliveryLocation2LineItems: [
    {
      itemNumber: 'bb3522fc-d0ab-4204-8ad6-0e44526054b2',
      itemName: 'a285ef50-12a4-4db9-b6e6-18f3955396b3',
      description: '9a40bb53-8027-414a-b012-6b7a8e6fc631',
      itemQty: '4e63bff9-1836-4a17-93c2-f6c35e394baa',
      unitPrice: '6c11593a-5e84-4df0-b7f0-8f7b9f2adbe4',
      lineTotal: null,
    },
  ],
  deliveryLocation3: {
    locationName: '36a746fb-74b0-4f02-a9aa-8b82a7300d5f',
    attn: '36fbcda9-7b13-476c-a1be-95ff4a26a0f5',
    companyName: '00c02b1a-9432-4d91-b8b7-78f4a252c40d',
    addressLine1: '81a602e4-e679-496b-8fc3-4797254b2b95',
    addressLine2: '8b79ea67-c3b0-49e4-91bc-d6f3f36a33b1',
    city: '3cd0029e-4c79-4a92-acc2-b4ae92c3c211',
    state: '9e4992b7-c24a-463d-898b-c6a349b8b60a',
    zipCode: 'c90196be-086c-4e35-980c-7a9e88d94d1e',
    country: 'dd632861-75a4-4a7c-bd08-421905235017',
    phoneNumber: 'ed6d02f3-12ce-472f-a1bc-43ef17e3a989',
    emailAddress: 'e1881bcb-1d13-4a61-9de3-d1fc239f6c49',
    locationTotal: 'a67fdf1c-58fc-4dfb-85c2-75d88f6d2054',
  },
  deliveryLocation3LineItems: [
    {
      itemNumber: '5f3474a7-0a95-45b7-9a78-3d75a389b2a3',
      itemName: 'a570bc24-c6c2-4296-9ad3-3e2cf53707b2',
      description: '4a2fbe77-d802-4e02-a08d-0037d9b53625',
      itemQty: '983e10e6-5cbe-4c8c-91df-07ea237bcde4',
      unitPrice: 'efeb5934-09fd-4b79-b45b-1877dc26bd26',
      lineTotal: null,
    },
  ],
  agreement: 'e4393f7b-152c-4d82-8cd8-c47e75a1c26d',
  instructions: '40ac88c9-b255-42a1-99fa-90dca1578e82',
} as const
